import React, { useEffect, useState } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';

const formatDate = iso => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString() + ' • ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return iso || '—';
    }
};

// ── Confirm Delete Modal ─────────────────────────────────────────────────────
const ConfirmDeleteModal = ({ tagName, onConfirm, onCancel }) => (
    <div style={modalStyles.overlay}>
        <div style={modalStyles.box}>
            <div style={modalStyles.header}>
                <div style={modalStyles.warningIcon}>
                    <i className="bi bi-exclamation-triangle" style={{ fontSize: 18, color: '#dc2626' }}></i>
                </div>
                <div>
                    <h6 style={modalStyles.title}>Delete Tag</h6>
                    <p style={modalStyles.subtitle}>This action cannot be undone</p>
                </div>
                <button style={modalStyles.closeBtn} onClick={onCancel}>
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>
            <div style={modalStyles.body}>
                <p style={modalStyles.message}>
                    Are you sure you want to delete the tag <strong>"{tagName}"</strong>?
                </p>
            </div>
            <div style={modalStyles.footer}>
                <button style={modalStyles.btnSecondary} onClick={onCancel}>Cancel</button>
                <button style={modalStyles.btnDanger} onClick={onConfirm}>
                    <i className="bi bi-trash" style={{ marginRight: 6 }}></i>
                    Yes, Delete
                </button>
            </div>
        </div>
    </div>
);

const modalStyles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    box: { background: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden' },
    header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#fff7f7' },
    warningIcon: { width: '38px', height: '38px', borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    title: { margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' },
    subtitle: { margin: 0, fontSize: '12px', color: '#9ca3af' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '13px', padding: 0, marginLeft: 'auto' },
    body: { padding: '20px' },
    message: { fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: '1.6' },
    footer: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #f3f4f6' },
    btnSecondary: { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnDanger: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

// ── Main Component ───────────────────────────────────────────────────────────
const TagsComponent = () => {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [newTag, setNewTag] = useState('');
    const [newTagError, setNewTagError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // holds tag item to delete

    useEffect(() => {
        let mounted = true;
        const fetchTags = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${url}/tags`, { headers: { Authorization: 'Bearer ' + token } });
                const data = await res.json();
                if (!mounted) return;
                const list = Array.isArray(data) ? data : (data.data || []);
                setTags(list.map(t => ({
                    id: t._id || t.id,
                    name: t.tag_name,
                    createdAt: t.createdAt || t.created_at || new Date().toISOString()
                })));
            } catch (e) {
                console.error('Failed to fetch tags', e);
                setTags([]);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchTags();
        return () => { mounted = false; };
    }, []);

    const filtered = tags.filter(t => t.name?.toLowerCase().includes(search.trim().toLowerCase()));

    const openAdd = () => { setNewTag(''); setNewTagError(''); setShowAdd(true); };
    const closeAdd = () => { setShowAdd(false); setNewTag(''); setNewTagError(''); };

    const handleAdd = async () => {
        const name = newTag.trim();
        if (!name) { setNewTagError('Tag name is required.'); return; }
        if (name.length > 20) { setNewTagError('Tag name cannot exceed 20 characters.'); return; }

        const tempId = 'tmp_' + Date.now();
        const tempItem = { id: tempId, name, createdAt: new Date().toISOString() };
        setTags(prev => [tempItem, ...prev]);
        setSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${url}/tags`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
                // Fixed typo: tag_nme → tag_name
                body: JSON.stringify({ tag_name: name })
            });

            if (!res.ok) {
                const body = await res.json().catch(() => null);
                const msg = body?.error || body?.message || 'Failed to create tag.';
                throw new Error(msg);
            }

            const saved = await res.json();
            const real = {
                id: saved._id || saved.id,
                name: saved.tag_name || name,
                createdAt: saved.createdAt || tempItem.createdAt
            };
            setTags(prev => prev.map(t => (t.id === tempId ? real : t)));
            toast.success('Tag added successfully!', { autoClose: 2000 });
            closeAdd();
        } catch (e) {
            console.error('Add tag failed', e);
            setTags(prev => prev.filter(t => t.id !== tempId));
            toast.error(e.message || 'An error occurred. Please try again.', { autoClose: 2500 });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        const item = confirmDelete;
        const backup = tags;
        setTags(prev => prev.filter(t => t.id !== item.id));
        setConfirmDelete(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${url}/tags/${item.id}`, {
                method: 'DELETE',
                headers: { Authorization: 'Bearer ' + token }
            });
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                throw new Error(body?.error || 'Delete failed.');
            }
            toast.success('Tag deleted successfully!', { autoClose: 2000 });
        } catch (e) {
            console.error('Delete failed', e);
            setTags(backup);
            toast.error(e.message || 'Failed to delete tag. Please try again.', { autoClose: 2500 });
        }
    };

    return (
        <WrapperComponent>
            <ToastContainer />
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Tags</h5>
                        <p style={styles.pageSubtitle}>Manage tags for your contacts and campaigns</p>
                    </div>
                    <button style={styles.btnPrimary} onClick={openAdd}>
                        <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>
                        Add Tag
                    </button>
                </div>

                {/* Search + count */}
                <div style={styles.searchRow}>
                    <div style={styles.searchWrapper}>
                        <i className="bi bi-search" style={styles.searchIcon}></i>
                        <input
                            type="text"
                            placeholder="Search tags..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={styles.searchInput}
                        />
                        {search && (
                            <button style={styles.clearBtn} onClick={() => setSearch('')}>
                                <i className="bi bi-x"></i>
                            </button>
                        )}
                    </div>
                    <span style={styles.resultCount}>
                        {filtered.length} tag{filtered.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {/* Tags grid */}
                <div style={styles.card}>
                    {loading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                            <p style={styles.stateText}>Loading tags…</p>
                        </div>
                    ) : tags.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-tags" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>No tags yet. Add your first tag.</p>
                            <button style={styles.btnPrimary} onClick={openAdd}>Add Tag</button>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-search" style={{ fontSize: 32, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>No tags match "{search}".</p>
                        </div>
                    ) : (
                        <div style={styles.tagsGrid}>
                            {filtered.map(t => (
                                <div key={t.id} style={styles.tagCard}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                                    <div style={styles.tagTop}>
                                        <div style={styles.tagIconWrap}>
                                            <i className="bi bi-tag-fill" style={{ fontSize: 13, color: '#1565c0' }}></i>
                                        </div>
                                        <button
                                            style={styles.deleteBtn}
                                            title="Delete tag"
                                            onClick={() => setConfirmDelete(t)}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                    <p style={styles.tagName}>{t.name}</p>
                                    <p style={styles.tagDate}>{formatDate(t.createdAt)}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add Tag Modal */}
            {showAdd && (
                <div style={modalStyles.overlay} onClick={closeAdd}>
                    <div style={{ ...modalStyles.box, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
                        <div style={{ ...modalStyles.header, background: '#f8fafc' }}>
                            <div style={{ ...modalStyles.warningIcon, background: '#eff6ff' }}>
                                <i className="bi bi-tag" style={{ fontSize: 16, color: '#1565c0' }}></i>
                            </div>
                            <div>
                                <h6 style={modalStyles.title}>Add New Tag</h6>
                                <p style={modalStyles.subtitle}>Tags help organise your contacts</p>
                            </div>
                            <button style={modalStyles.closeBtn} onClick={closeAdd}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div style={modalStyles.body}>
                            <label style={styles.label}>
                                Tag Name
                                <span style={styles.charCount}>{20 - newTag.length}/20</span>
                            </label>
                            <input
                                autoFocus
                                type="text"
                                value={newTag}
                                maxLength={20}
                                placeholder="Enter tag name"
                                style={{ ...styles.input, ...(newTagError ? styles.inputError : {}) }}
                                onChange={e => { setNewTag(e.target.value); setNewTagError(''); }}
                                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') closeAdd(); }}
                            />
                            {newTagError && <span style={styles.fieldError}>{newTagError}</span>}
                        </div>
                        <div style={modalStyles.footer}>
                            <button style={modalStyles.btnSecondary} onClick={closeAdd} disabled={submitting}>Cancel</button>
                            <button style={styles.btnPrimary} onClick={handleAdd}
                                disabled={submitting || !newTag.trim()}>
                                {submitting
                                    ? <><span style={styles.spinner}></span> Adding…</>
                                    : <><i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>Add Tag</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Delete Modal */}
            {confirmDelete && (
                <ConfirmDeleteModal
                    tagName={confirmDelete.name}
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    searchRow: { display: 'flex', alignItems: 'center', gap: '12px' },
    searchWrapper: { position: 'relative', flex: 1, maxWidth: '360px' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px' },
    searchInput: { width: '100%', padding: '9px 36px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', color: '#111827' },
    clearBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '15px', padding: 0, display: 'flex', alignItems: 'center' },
    resultCount: { fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', minHeight: '200px' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    tagsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' },
    tagCard: { background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '6px', transition: 'transform 0.15s, box-shadow 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', cursor: 'default' },
    tagTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    tagIconWrap: { width: '28px', height: '28px', borderRadius: '7px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '14px', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', transition: 'color 0.15s, background 0.15s' },
    tagName: { margin: 0, fontSize: '14px', fontWeight: '600', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    tagDate: { margin: 0, fontSize: '11px', color: '#9ca3af' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
    charCount: { fontSize: '11px', color: '#9ca3af', fontWeight: 400 },
    input: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827', fontFamily: 'inherit' },
    inputError: { borderColor: '#ef4444' },
    fieldError: { fontSize: '12px', color: '#ef4444', display: 'block', marginTop: 4 },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    spinner: { width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite', marginRight: 6 },
};

export default TagsComponent;
